import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Clock3,
  Home,
  Link2,
  MessageCircleOff,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { motion } from 'framer-motion';
import MessageMarkdown from '../components/chat/MessageMarkdown';
import { getSharedChatByToken } from '../api/api';

export default function SharedChatView({ isDarkMode = false }) {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [sharedPayload, setSharedPayload] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadSharedChat = async () => {
      if (!token) {
        setErrorType('invalid');
        setErrorMessage('Invalid share link.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorType('');
        setErrorMessage('');

        const data = await getSharedChatByToken(token);
        const resolvedMessages = Array.isArray(data?.messages)
          ? data.messages
          : [];

        const normalizedMessages = resolvedMessages
          .filter((message) => message?.content)
          .map((message, index) => ({
            id: message?.id ?? index + 1,
            role: message?.role ?? 'system',
            content: message?.content ?? '',
            timestamp: message?.timestamp ?? data?.created_at ?? null,
          }));

        const normalized = {
          title:
            data?.title ||
            data?.chat_title ||
            data?.chat_session?.title ||
            'Shared Conversation',
          created_at: data?.created_at || null,
          messages: normalizedMessages,
        };

        if (isMounted) {
          setSharedPayload(normalized);
        }
      } catch (error) {
        if (!isMounted) return;

        if (error?.response?.status === 404) {
          setErrorType('invalid');
          setErrorMessage('This share link does not exist or was removed.');
        } else {
          setErrorType('generic');
          setErrorMessage(
            error?.response?.data?.detail ||
              error?.response?.data?.message ||
              'Unable to load this shared chat right now.',
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSharedChat();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const messages = useMemo(
    () => sharedPayload?.messages || [],
    [sharedPayload],
  );
  const title = sharedPayload?.title || 'Shared Conversation';
  const createdAt = sharedPayload?.created_at
    ? new Date(sharedPayload.created_at).toLocaleString()
    : null;
  const showErrorState = !loading && Boolean(errorMessage);
  const showFooterCta = !loading && !showErrorState;

  const SkeletonCard = ({ className = '' }) => (
    <div
      className={`animate-pulse rounded-2xl border ${
        isDarkMode
          ? 'border-slate-800 bg-slate-900/70'
          : 'border-slate-200 bg-white/90'
      } ${className}`}
    />
  );

  return (
    <div
      className={`relative min-h-screen overflow-hidden ${
        isDarkMode ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'
      }`}
    >
      <div
        className={`pointer-events-none absolute inset-0 ${
          isDarkMode
            ? 'bg-[radial-gradient(circle_at_10%_10%,rgba(117,126,240,0.25),transparent_40%),radial-gradient(circle_at_90%_0%,rgba(34,197,94,0.16),transparent_30%)]'
            : 'bg-[radial-gradient(circle_at_10%_10%,rgba(71,83,251,0.18),transparent_40%),radial-gradient(circle_at_90%_0%,rgba(16,185,129,0.14),transparent_30%)]'
        }`}
      />

      <div className="relative mx-auto max-w-6xl px-4 pb-10 pt-6 sm:px-6 lg:px-8">
        {loading ? (
          <div
            className={`mb-6 overflow-hidden rounded-3xl border ${
              isDarkMode
                ? 'border-slate-800 bg-slate-900/80 backdrop-blur'
                : 'border-slate-200 bg-white/85 backdrop-blur'
            }`}
          >
            <div
              className={`border-b px-6 py-5 ${
                isDarkMode ? 'border-slate-800' : 'border-slate-200'
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <div className="h-7 w-36 animate-pulse rounded-full bg-slate-300/30" />
                <div className="h-7 w-28 animate-pulse rounded-full bg-slate-300/30" />
              </div>
              <div className="mt-4 h-10 w-96 max-w-full animate-pulse rounded bg-slate-300/30" />
              <div className="mt-3 h-4 w-full max-w-3xl animate-pulse rounded bg-slate-300/30" />
              <div className="mt-2 h-4 w-72 max-w-full animate-pulse rounded bg-slate-300/30" />
            </div>

            <div className="grid gap-3 px-6 py-4 sm:grid-cols-3">
              <SkeletonCard className="px-4 py-3">
                <div className="h-3 w-20 rounded bg-slate-300/30" />
                <div className="mt-3 h-6 w-16 rounded bg-slate-300/30" />
              </SkeletonCard>
              <SkeletonCard className="px-4 py-3">
                <div className="h-3 w-16 rounded bg-slate-300/30" />
                <div className="mt-3 h-6 w-28 rounded bg-slate-300/30" />
              </SkeletonCard>
              <SkeletonCard className="px-4 py-3">
                <div className="h-3 w-24 rounded bg-slate-300/30" />
                <div className="mt-3 h-6 w-24 rounded bg-slate-300/30" />
              </SkeletonCard>
            </div>
          </div>
        ) : !showErrorState ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className={`mb-6 overflow-hidden rounded-3xl border ${
              isDarkMode
                ? 'border-slate-800 bg-slate-900/80 backdrop-blur'
                : 'border-slate-200 bg-white/85 backdrop-blur'
            }`}
          >
            <div
              className={`border-b px-6 py-5 ${
                isDarkMode ? 'border-slate-800' : 'border-slate-200'
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                    isDarkMode
                      ? 'bg-indigo-500/20 text-indigo-200'
                      : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  <Sparkles size={13} />
                  Shared Snapshot
                </span>

                <span
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${
                    isDarkMode
                      ? 'bg-emerald-500/15 text-emerald-200'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  <ShieldCheck size={13} />
                  Read-only
                </span>
              </div>

              <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
                {title}
              </h1>

              <p
                className={`mt-2 max-w-3xl text-sm leading-6 ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}
              >
                This link opens a public, non-editable snapshot of the
                conversation.
              </p>
            </div>

            <div className="grid gap-3 px-6 py-4 sm:grid-cols-3">
              <div
                className={`rounded-2xl border px-4 py-3 ${
                  isDarkMode
                    ? 'border-slate-800 bg-slate-900'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p
                  className={`text-xs uppercase tracking-wide ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Messages
                </p>
                <p className="mt-1 text-lg font-semibold">
                  {showErrorState ? '-' : messages.length}
                </p>
              </div>

              <div
                className={`rounded-2xl border px-4 py-3 ${
                  isDarkMode
                    ? 'border-slate-800 bg-slate-900'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p
                  className={`text-xs uppercase tracking-wide ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Access
                </p>
                <p className="mt-1 text-lg font-semibold">Public Link</p>
              </div>

              <div
                className={`rounded-2xl border px-4 py-3 ${
                  isDarkMode
                    ? 'border-slate-800 bg-slate-900'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p
                  className={`text-xs uppercase tracking-wide ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Shared At
                </p>
                <p className="mt-1 flex items-center gap-1 text-sm font-medium">
                  <Clock3 size={14} />
                  {createdAt || (showErrorState ? '-' : 'Not available')}
                </p>
              </div>
            </div>
          </motion.div>
        ) : null}

        {loading && (
          <div className="space-y-4">
            <SkeletonCard className="p-5">
              <div className="h-3 w-24 rounded bg-slate-300/30" />
              <div className="mt-3 h-6 w-72 rounded bg-slate-300/30" />
              <div className="mt-3 h-4 w-full max-w-2xl rounded bg-slate-300/30" />
              <div className="mt-2 h-4 w-2/3 rounded bg-slate-300/30" />
            </SkeletonCard>

            <SkeletonCard className="p-5">
              <div className="flex items-center justify-between">
                <div className="h-4 w-20 rounded bg-slate-300/30" />
                <div className="h-3 w-28 rounded bg-slate-300/30" />
              </div>
              <div className="mt-4 h-4 w-full rounded bg-slate-300/30" />
              <div className="mt-2 h-4 w-[92%] rounded bg-slate-300/30" />
              <div className="mt-2 h-4 w-[78%] rounded bg-slate-300/30" />
            </SkeletonCard>

            <SkeletonCard className="p-5">
              <div className="flex items-center justify-between">
                <div className="h-4 w-28 rounded bg-slate-300/30" />
                <div className="h-3 w-24 rounded bg-slate-300/30" />
              </div>
              <div className="mt-4 h-4 w-full rounded bg-slate-300/30" />
              <div className="mt-2 h-4 w-[88%] rounded bg-slate-300/30" />
            </SkeletonCard>
          </div>
        )}

        {showErrorState && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className={`mx-auto mb-6 mt-2 max-w-2xl overflow-hidden rounded-3xl border text-center ${
              isDarkMode
                ? 'border-slate-800 bg-slate-900/90'
                : 'border-slate-200 bg-white'
            }`}
          >
            <div
              className={`h-2 w-full ${
                errorType === 'invalid'
                  ? isDarkMode
                    ? 'bg-amber-400/70'
                    : 'bg-amber-400'
                  : isDarkMode
                    ? 'bg-rose-400/70'
                    : 'bg-rose-400'
              }`}
            />

            <div className="px-7 pb-7 pt-6">
              <span
                className={`mx-auto inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                  errorType === 'invalid'
                    ? isDarkMode
                      ? 'bg-amber-500/15 text-amber-300'
                      : 'bg-amber-100 text-amber-800'
                    : isDarkMode
                      ? 'bg-rose-500/15 text-rose-300'
                      : 'bg-rose-100 text-rose-800'
                }`}
              >
                {errorType === 'invalid' ? (
                  <MessageCircleOff size={13} />
                ) : (
                  <AlertTriangle size={13} />
                )}
                Link Issue
              </span>

            <div
              className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${
                errorType === 'invalid'
                  ? isDarkMode
                    ? 'bg-amber-500/15 text-amber-300'
                    : 'bg-amber-100 text-amber-700'
                  : isDarkMode
                    ? 'bg-rose-500/15 text-rose-300'
                    : 'bg-rose-100 text-rose-700'
              }`}
            >
              {errorType === 'invalid' ? (
                <MessageCircleOff size={26} />
              ) : (
                <AlertTriangle size={26} />
              )}
            </div>

            <h2 className="text-2xl font-semibold tracking-tight">
              {errorType === 'invalid'
                ? 'Share Link Not Found'
                : 'Unable To Load Shared Chat'}
            </h2>

            <p
              className={`mx-auto mt-3 max-w-xl text-sm leading-6 ${
                isDarkMode ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              {errorMessage}
            </p>

              <p
                className={`mx-auto mt-2 max-w-xl text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                It may be expired, revoked, or copied incompletely.
              </p>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/"
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                    isDarkMode
                      ? 'bg-indigo-500/20 text-indigo-200 hover:bg-indigo-500/30'
                      : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
                  }`}
                >
                  <Home size={14} />
                  Open Home
                </Link>

                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                    isDarkMode
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <RefreshCw size={14} />
                  Try Again
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {!loading && !showErrorState && messages.length === 0 && (
          <div
            className={`rounded-2xl border px-5 py-6 text-sm ${
              isDarkMode
                ? 'border-slate-800 bg-slate-900 text-slate-300'
                : 'border-slate-200 bg-white text-slate-600'
            }`}
          >
            This shared conversation is empty.
          </div>
        )}

        {!loading && !showErrorState && messages.length > 0 && (
          <div className="space-y-4">
            {messages.map((message, index) => {
              const isUser = message.role === 'user';

              return (
                <motion.div
                  key={message.id ?? index}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.35,
                    delay: Math.min(index * 0.04, 0.2),
                    ease: 'easeOut',
                  }}
                  className={`rounded-2xl border ${
                    isDarkMode
                      ? 'border-slate-800 bg-slate-900/90'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div
                    className={`flex items-center justify-between rounded-t-2xl border-b px-4 py-3 ${
                      isDarkMode
                        ? 'border-slate-800 bg-slate-900'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <span className="text-sm font-medium">
                      {isUser ? 'User' : 'Ulegal Assistant'}
                    </span>
                    <span
                      className={`text-xs ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      {message?.timestamp
                        ? new Date(message.timestamp).toLocaleString()
                        : ''}
                    </span>
                  </div>

                  <div className="px-5 py-4">
                    {isUser ? (
                      <p
                        className={`whitespace-pre-wrap text-sm leading-7 ${
                          isDarkMode ? 'text-slate-100' : 'text-slate-900'
                        }`}
                      >
                        {message.content}
                      </p>
                    ) : (
                      <MessageMarkdown
                        content={message.content}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {showFooterCta && (
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                isDarkMode
                  ? 'bg-indigo-500/20 text-indigo-200 hover:bg-indigo-500/30'
                  : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
              }`}
            >
              Open uLegalAI
            </Link>

            <span
              className={`inline-flex items-center gap-1 text-xs ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              <Link2 size={12} />
              Share link is view-only
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
